import {
  VStack,
  Center,
  Text,
  Box,
  HStack,
  Pressable,
  Image,
  Spinner,
} from 'native-base';
import { useEffect, useState } from 'react';
import { getDateFormat, getHourFormat } from '../helper/date.helper';
import CircleSvg from '../svg/circle';
import OkSvg from '../svg/ok';
import { TrackingEvent, TrackingResult } from '../types';
import { TrackingNumberResultHash } from '../screens/ResultScreen';
import { getHostname } from '../config';
import { toCamelKeys } from 'keys-transform';
import CustomTextSkeleton from './CustomSkeketonText';
import { useTranslation } from 'react-i18next';
import { PodModal } from './PodModal';

function camelize<T = any>(data: any): T {
  return toCamelKeys(data) as T;
}

export type Props = {
  trackingNumberResultHash: TrackingNumberResultHash;
  trackingNumberResultFetchHandler: (
    trackingNumber: TrackingNumberResultHash['trackingNumber'],
    result: TrackingResult | 'loading' | undefined,
  ) => any;
  setOpenDialog: (state: boolean) => any;
  setImage: (img: string) => any;
};

//509998770010
//990071619234
const fetchTrackingData = async (
  trackingNumber: string,
): Promise<TrackingResult | undefined> => {
  try {
    const response = await fetch(
      `${getHostname(
        window.location.hostname,
      )}/api/v1/requests?q=${trackingNumber}`,
      // `https://api.a2a56.com/api/v1/requests?q=${trackingNumber}`
    );
    const data = await response.json();
    return camelize<TrackingResult>(data);
  } catch (e) {
    return undefined;
  }
};

const fetchPodImages = async (
  podUrls: string[],
  platform: string,
): Promise<string[]> => {
  try {
    const response = await fetch(
      `${getHostname(window.location.hostname)}/api/v1/pod?platform=${platform}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(podUrls),
      },
    );

    if (!response.ok) {
      throw new Error('Failed to fetch POD images');
    }

    const data = await response.json();
    return data; // Assuming the API returns an array of base64 strings
  } catch (e) {
    console.error('Error fetching POD images:', e);
    return [];
  }
};

const TrackingResultRow: React.FC<Props> = ({
  trackingNumberResultHash,
  trackingNumberResultFetchHandler,
  setImage,
  setOpenDialog,
}) => {
  const [showEventList, setShowEventList] = useState<boolean>(false);

  useEffect(() => {
    const fetchStatus = trackingNumberResultHash.fetchStatus;
    if (
      fetchStatus === 'error' ||
      fetchStatus === 'success' ||
      fetchStatus === 'loading'
    ) {
      return;
    }

    trackingNumberResultFetchHandler(
      trackingNumberResultHash.trackingNumber,
      'loading',
    );

    fetchTrackingData(trackingNumberResultHash.trackingNumber)
      .then(result => {
        trackingNumberResultFetchHandler(
          trackingNumberResultHash.trackingNumber,
          result,
        );
      })
      .catch(e => {
        console.error(e);
        trackingNumberResultFetchHandler(
          trackingNumberResultHash.trackingNumber,
          undefined,
        );
      });

    // return () => {
    //   shouldUpdate = false;
    // };
  }, [trackingNumberResultHash, trackingNumberResultFetchHandler]);

  const trackingResult = trackingNumberResultHash.trackingResult;
  const error = trackingNumberResultHash.fetchStatus === 'error';

  return (
    // <Box
    //   p={4}
    //   width="911px"
    //   borderRadius={10}
    //   mb={2}
    //   borderWidth={1}
    //   borderColor="#d1d5db">
    <div
      style={{
        boxShadow: '0px 8px 20px #0000000D',
        width: window.innerWidth * 0.7 - 322 - 30 + 'px',
        borderRadius: '20px',
        padding: '12px',
        border: '1px solid #d1d5db',
        maxWidth: '911px',
        marginBottom: '10px',
      }}>
      <HStack alignItems="center" justifyContent="space-between">
        <HStack alignItems="center">
          <Pressable
            //@ts-ignore
            onPress={() => {
              if (error || trackingResult!.status === 'not-found') {
                return;
              }
              setShowEventList(!showEventList);
            }}>
            <HStack>
              {trackingNumberResultHash.trackingResult?.delivered && (
                <Image
                  source={{
                    uri: `${process.env.PUBLIC_URL}/icons/delivered_background.svg`,
                  }}
                  alt="no_trace_background_icon"
                  width={9}
                  height={8}
                />
              )}
              {trackingNumberResultHash.trackingResult?.status ===
                'in_transit' && (
                <Image
                  source={{
                    uri: `${process.env.PUBLIC_URL}/icons/transit_background.svg`,
                  }}
                  alt="no_trace_background_icon"
                  width={9}
                  height={8}
                />
              )}
              {trackingNumberResultHash.trackingResult?.status ===
                'expired' && (
                <Image
                  source={{
                    uri: `${process.env.PUBLIC_URL}/icons/expired_background.svg`,
                  }}
                  alt="no_trace_background_icon"
                  width={9}
                  height={8}
                />
              )}
              {trackingNumberResultHash.trackingResult?.status === 'alert' && (
                <Image
                  source={{
                    uri: `${process.env.PUBLIC_URL}/icons/alert_background.svg`,
                  }}
                  alt="no_trace_background_icon"
                  width={9}
                  height={8}
                />
              )}
              {!trackingNumberResultHash.trackingResult?.delivered &&
                trackingNumberResultHash.trackingResult?.status !==
                  'in_transit' &&
                trackingNumberResultHash.trackingResult?.status !== 'expired' &&
                trackingNumberResultHash.trackingResult?.status !== 'alert' && (
                  <Image
                    source={{
                      uri: `${process.env.PUBLIC_URL}/icons/no_trace_background.svg`,
                    }}
                    alt="no_trace_background_icon"
                    width={9}
                    height={8}
                  />
                )}
              <Text ml={6} bold fontSize="20px" color="#293D8B">
                {trackingNumberResultHash.trackingNumber}
              </Text>
            </HStack>
          </Pressable>
        </HStack>
      </HStack>
      <TrackingResultRowContent
        trackingNumberResultHash={trackingNumberResultHash}
        showEventList={showEventList}
        setOpenDialog={setOpenDialog}
        setImage={setImage}
      />
    </div>

    // </Box>
  );
};

const TrackingResultRowContent: React.FC<{
  trackingNumberResultHash: TrackingNumberResultHash;
  showEventList: boolean;
  setOpenDialog: (state: boolean) => any;
  setImage: (img: string) => any;
}> = ({ trackingNumberResultHash, showEventList, setOpenDialog, setImage }) => {
  const trackingResult = trackingNumberResultHash.trackingResult;
  const error = trackingNumberResultHash.fetchStatus === 'error';
  const success = trackingNumberResultHash.fetchStatus === 'success';
  const isLoaded = success || error;
  const events = trackingResult?.data?.events || [];
  const { t } = useTranslation();

  // POD modal state
  const [showPodModal, setShowPodModal] = useState(false);
  const [podImages, setPodImages] = useState<string[]>([]);
  const [loadingPod, setLoadingPod] = useState(false);

  const handlePodClick = async (podUrls: string[]) => {
    if (!podUrls || podUrls.length === 0) return;

    setLoadingPod(true);
    try {
      const images = await fetchPodImages(podUrls, trackingResult?.carrier || 'savar');
      setPodImages(images);
      setShowPodModal(true);
    } catch (error) {
      console.error('Error loading POD images:', error);
    } finally {
      setLoadingPod(false);
    }
  };

  const getCountry = (country: string | null): string | null => {
    if (!country) {
      return null;
    }
    switch (country.toLowerCase()) {
      case 'china':
        return 'CN';
      case 'mexico':
        return 'MX';
      case 'chile':
        return 'CL';
      case 'republica dominicana':
        return 'DO';
      case 'argentina':
        return 'AR';
      default:
        return country;
    }
  };

  return (
    <>
      <HStack
        pl={9}
        pr={9}
        justifyContent="space-between"
        alignItems="flex-start">
        <Box width={120}>
          {trackingResult?.delivered ? (
            <HStack
              width="106px"
              height="25px"
              backgroundColor="#c5f9d5"
              borderRadius={14}
              alignItems="center"
              justifyContent="center">
              <OkSvg />
              <Text ml={1} fontFamily="Segoe UI" color="#009c30">
                {t('delivered')}
              </Text>
            </HStack>
          ) : (
            <CustomTextSkeleton
              bold
              loading={!isLoaded}
              placeholderText={t('loading_package')}
              text={(() => {
                if (error || trackingResult?.status === 'not-found') {
                  return t('not_found');
                }

                if (success) {
                  return t(trackingResult?.status || '') || '';
                }

                return '';
              })()}
            />
          )}
        </Box>
        {events.length > 1 && (
          <HStack
            width="255px"
            alignItems="stretch"
            justifyContent="space-evenly">
            <VStack>
              <CustomTextSkeleton
                color="#000000"
                loading={!isLoaded}
                placeholderText={'2022-30-30'}
                fontFamily="Segoe UI"
                text={
                  (events.length > 0 &&
                    getDateFormat(events[events.length - 1].datetime)) ||
                  ''
                }
              />
              <CustomTextSkeleton
                color="#aaaaaa"
                loading={!isLoaded}
                placeholderText={'2022-30-30'}
                fontFamily="Segoe UI"
                text={
                  (events.length > 0 &&
                    getHourFormat(events[events.length - 1].datetime)) ||
                  ''
                }
              />
            </VStack>
            <Box width="2px" backgroundColor="#d3d2d2" />
            <VStack>
              <CustomTextSkeleton
                color="#000000"
                loading={!isLoaded}
                placeholderText={'2022-30-30'}
                fontFamily="Segoe UI"
                text={
                  (events.length > 0 &&
                    getDateFormat(trackingResult!.data.events[0].datetime)) ||
                  ''
                }
              />
              <CustomTextSkeleton
                color="#aaaaaa"
                loading={!isLoaded}
                placeholderText={'2022-30-30'}
                fontFamily="Segoe UI"
                text={
                  (events.length > 0 &&
                    getHourFormat(trackingResult!.data.events[0].datetime)) ||
                  ''
                }
              />
            </VStack>
          </HStack>
        )}

        {events.length === 1 && (
          <HStack width="255px" alignItems="stretch" justifyContent="center">
            <VStack>
              <CustomTextSkeleton
                color="#000000"
                loading={!isLoaded}
                placeholderText={'2022-30-30'}
                fontFamily="Segoe UI"
                text={
                  (events.length > 0 &&
                    getDateFormat(events[events.length - 1].datetime)) ||
                  ''
                }
              />
              <CustomTextSkeleton
                color="#aaaaaa"
                loading={!isLoaded}
                placeholderText={'2022-30-30'}
                fontFamily="Segoe UI"
                text={
                  (events.length > 0 &&
                    getHourFormat(events[events.length - 1].datetime)) ||
                  ''
                }
              />
            </VStack>
          </HStack>
        )}

        <Center>
          <HStack width="153px">
            <CustomTextSkeleton
              loading={!isLoaded}
              placeholderText={t('origin')}
              fontFamily="Segoe UI"
              color="#000000"
              flag
              text={success ? getCountry(trackingResult!.from) || '' : ''}
            />
            <VStack alignItems="center">
              <Box top={0}>
                <Image
                  source={{
                    uri: `${process.env.PUBLIC_URL}/icons/flag_arrow.svg`,
                  }}
                  alt="right_arrow"
                  width={5}
                  height={5}
                />
              </Box>
              <CustomTextSkeleton
                loading={!isLoaded}
                placeholderText={t('carrier')}
                fontFamily="Segoe UI"
                color="#000000"
                text={trackingResult?.carrier?.toUpperCase() || t('unknown')}
              />
            </VStack>
            <CustomTextSkeleton
              loading={!isLoaded}
              placeholderText={t('destination')}
              fontFamily="Segoe UI"
              color="#000000"
              flag
              text={success ? getCountry(trackingResult!.to) || '' : ''}
            />
          </HStack>
        </Center>

        {trackingResult && trackingResult.pod && (
          <button
            onClick={() => {
              setImage(trackingResult.pod || '');
              setOpenDialog(true);
            }}
            style={{
              backgroundColor: '#10b981',
              border: 'none',
              padding: '8px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = '#059669';
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow =
                '0 4px 12px rgba(16, 185, 129, 0.4)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = '#10b981';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow =
                '0 2px 8px rgba(16, 185, 129, 0.3)';
            }}>
            <Image
              source={{
                uri: `${process.env.PUBLIC_URL}/icons/eye-svgrepo-com.svg`,
              }}
              alt="right_arrow"
              width={5}
              height={5}
            />
          </button>
        )}
      </HStack>
      {showEventList && (
        <EventList
          events={events}
          onPodClick={handlePodClick}
          loadingPod={loadingPod}
        />
      )}
      {showPodModal && (
        <PodModal images={podImages} close={() => setShowPodModal(false)} />
      )}
    </>
  );
};

const EventList: React.FC<{
  events: TrackingEvent[];
  onPodClick: (podUrls: string[]) => void;
  loadingPod: boolean;
}> = ({ events, onPodClick, loadingPod }) => {
  const { t } = useTranslation();
  return (
    <VStack pt={4}>
      {events.map((e, i) => {
        return (
          <HStack key={i} width="70%" alignItems="flex-start">
            <VStack ml={5} alignItems="center" justifyContent="flex-start">
              <Box mt="6px">
                <CircleSvg></CircleSvg>
              </Box>
              {i < events.length - 1 && (
                <div
                  style={{
                    width: '2px',
                    height: '40px',
                    borderLeft: '2px dashed #d1d5db',
                    marginTop: '6px',
                  }}
                />
              )}
            </VStack>
            <HStack
              ml={3}
              width="550px"
              justifyContent="space-between"
              alignItems="center">
              <Text fontSize="15px" fontFamily="Segoe UI" color="#4c66fb">
                {e.datetime}
              </Text>
              <HStack
                width="50%"
                justifyContent="space-between"
                alignItems="center">
                <VStack width="80%" alignItems="center">
                  <Text
                    width="100%"
                    textAlign="left"
                    fontSize="15px"
                    fontFamily="Segoe UI"
                    bold
                    color="#000000">
                    {e.description}
                  </Text>
                  {e.location && (
                    <Text
                      fontSize="15px"
                      fontFamily="Segoe UI"
                      textTransform="uppercase"
                      color="#000000">
                      {e.location}
                    </Text>
                  )}
                </VStack>
                {e.pod && e.pod.length > 0 && (
                  <button
                    onClick={() => onPodClick(e.pod!)}
                    disabled={loadingPod}
                    style={{
                      backgroundColor: loadingPod ? '#9ca3af' : '#3b82f6',
                      border: 'none',
                      padding: '6px',
                      borderRadius: '8px',
                      opacity: loadingPod ? 0.6 : 1,
                      cursor: loadingPod ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease',
                      boxShadow: loadingPod
                        ? '0 2px 4px rgba(0,0,0,0.1)'
                        : '0 2px 8px rgba(59, 130, 246, 0.3)',
                    }}
                    onMouseEnter={e => {
                      if (!loadingPod) {
                        e.currentTarget.style.backgroundColor = '#2563eb';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow =
                          '0 4px 12px rgba(59, 130, 246, 0.4)';
                      }
                    }}
                    onMouseLeave={e => {
                      if (!loadingPod) {
                        e.currentTarget.style.backgroundColor = '#3b82f6';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow =
                          '0 2px 8px rgba(59, 130, 246, 0.3)';
                      }
                    }}>
                    {loadingPod ? (
                      <Spinner size="sm" color="white" />
                    ) : (
                      <Image
                        source={{
                          uri: `${process.env.PUBLIC_URL}/icons/download.svg`,
                        }}
                        alt="download"
                        width={4}
                        height={4}
                        style={{ filter: 'brightness(0) invert(1)' }}
                      />
                    )}
                  </button>
                )}
              </HStack>
            </HStack>
          </HStack>
        );
      })}
    </VStack>
  );
};

export default TrackingResultRow;
